{********************************************************************}
{                                                                    }
{ written by TMS Software                                            }
{            Copyright (c) 2020 - 2026                               }
{            Email : info@tmssoftware.com                            }
{            Web : http://www.tmssoftware.com                        }
{                                                                    }
{ The source code is given as is. The author is not responsible      }
{ for any possible damage done due to the use of this code.          }
{ The complete source code remains property of the author and may    }
{ not be distributed, published, given or sold in any form as such.  }
{ No parts of the source code can be included in any other component }
{ or application without written authorization of the author.        }
{********************************************************************}

unit WEBLib.SignatureCapture;

{$DEFINE NOPP}

interface

uses
  Classes, WEBLib.Controls, Types, Contnrs, WEBLib.Graphics, web;

type
  TSignatureCaptureLineTextPosition = (ltpBottomLeft, ltpBottomCenter, ltpBottomRight, ltpTopLeft, ltpTopCenter, ltpTopRight, ltpXY);

  TSignatureCaptureSignTextPosition = class(TPersistent)
  private
    FLineVisible: Boolean;
    FOnChange: TNotifyEvent;
    FPosition: TSignatureCaptureLineTextPosition;
    FX: Integer;
    FY: Integer;
    FLine: TPen;
    procedure SetLineVisible(const Value: Boolean);
    procedure SetPosition(const Value: TSignatureCaptureLineTextPosition);
    procedure SetX(const Value: Integer);
    procedure SetY(const Value: Integer);
    procedure SetLine(const Value: TPen);
  protected
    procedure Changed; virtual;
  public
    constructor Create;
    property OnChange: TNotifyEvent read FOnChange write FOnChange;
  published
    property Line: TPen read FLine write SetLine;
    property LineVisible: Boolean read FLineVisible write SetLineVisible;
    property Position: TSignatureCaptureLineTextPosition read FPosition write SetPosition;
    property X: Integer read FX write SetX;
    property Y: Integer read FY write SetY;
  end;

  TSignatureCapturePoint = class(TObject)
  public
    X, Y, PenWidth: Integer;
    DrawLine: Boolean;
    PenColor: TColor;
    PenStyle: TPenStyle;
  end;

  TSignatureCapturePoints = class(TObjectList)
  private
    function GetItem(Index: Integer): TSignatureCapturePoint; reintroduce;
    procedure SetItem(Index: Integer; const Value: TSignatureCapturePoint);
  public
    property Items[Index: Integer]: TSignatureCapturePoint read GetItem write SetItem; default;
  end;

  TSignatureCaptureClearButtonPosition = (cbpTopLeft, cbpTopRight, cbpBottomLeft, cbpBottomRight, cbpXY);

  TSignatureCaptureClearButton = class(TPersistent)
  private
    FImage: TURLPicture;
    FX: Integer;
    FY: Integer;
    FPosition: TSignatureCaptureClearButtonPosition;
    FOnChange: TNotifyEvent;
    procedure SetImage(const Value: TURLPicture);
    procedure SetPosition(const Value: TSignatureCaptureClearButtonPosition);
    procedure SetX(const Value: Integer);
    procedure SetY(const Value: Integer);
  protected
    procedure Changed; virtual;
    procedure ImageChange(Sender: TObject);
  public
    constructor Create;
    destructor Destroy; override;
    property OnChange: TNotifyEvent read FOnChange write FOnChange;
  published
    property Image: TURLPicture read FImage write SetImage;
    property Position: TSignatureCaptureClearButtonPosition read FPosition write SetPosition;
    property X: Integer read FX write SetX;
    property Y: Integer read FY write SetY;
  end;

  TSignatureCapture = class(TCustomControl)
  private
    FExport: Boolean;
    FClearBtn: TGraphic;
    FMouseDown: Boolean;
    FSignaturePoints: TSignatureCapturePoints;
    FTextPosition: TSignatureCaptureSignTextPosition;
    FPen: TPen;
    FClearButton: TSignatureCaptureClearButton;
    FText: string;
    FOnClear: TNotifyEvent;
    FMinSignaturePoints: integer;
    procedure SetTextPosition(const Value: TSignatureCaptureSignTextPosition);
    procedure SetPen(const Value: TPen);
    procedure SetClearButton(const Value: TSignatureCaptureClearButton);
    function GetEmpty: Boolean;
    procedure SetEmpty(const Value: Boolean);
    procedure SetText(const Value: string);
  protected
    procedure CreateInitialize; override;
    procedure MouseUp(Button: TMouseButton; Shift: TShiftState; X,Y: Integer); override;
    procedure MouseDown(Button: TMouseButton; Shift: TShiftState; X,Y: Integer); override;
    procedure MouseMove(Shift: TShiftState; X,Y: Integer); override;
    procedure Paint; override;
    procedure PaintSignature(ACanvas: TCanvas); virtual;
    procedure PaintClearButton; virtual;
    procedure PaintLine; virtual;
    procedure PaintText; virtual;
    procedure ClearBtnChanged(Sender: TObject);
    procedure ButtonChanged(Sender: TObject);
    procedure TextPositionChanged(Sender: TObject);
    function GetLineY: Integer;
    function InClearRect(AX, AY: Integer): Boolean;
    procedure UpdateElement; override;
  public
    destructor Destroy; override;
    function GetAsBase64Image: string;
    property Empty: Boolean read GetEmpty write SetEmpty;
    property MinSignaturePoints: integer read FMinSignaturePoints write FMinSignaturePoints;
  published
    property Align;
    property Anchors;
    property ChildOrder;
    property ShowHint;
    property Visible;
    property TabOrder;
    property TabStop;
    property ClearButton: TSignatureCaptureClearButton read FClearButton write SetClearButton;
    property TextPosition: TSignatureCaptureSignTextPosition read FTextPosition write SetTextPosition;
    property Text: string read FText write SetText;
    property Pen: TPen read FPen write SetPen;
    property OnClear: TNotifyEvent read FOnClear write FOnClear;
    property OnEnter;
    property OnExit;
    property OnMouseDown;
    property OnMouseEnter;
    property OnMouseLeave;
    property OnMouseMove;
    property OnMouseUp;
  end;

  TWebSignatureCapture = class(TSignatureCapture);

implementation

const
  TEXTSPACING = 5;
  TSIGNATURECAPTURECLEAR = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABgAAAAYCAYAAADgdz34AAAArklEQVRIS+2U0Q2DQAxDH0swRLdop2AIxukQnaJsw' +
                           'RBMgSxBhdCF+CdSP7ifSCfHTny5dBSfrpifWyB1OLLoAbyBAVgSlh74ACMwn7GRwBd4bgmKkYjIJ0AFKb5cgWOiqmqJOJjLKboisMjVTTamLSLl' +
                           '7bZE3f2cygQEPIvoTp6n5E4HeyVHEd1Z5H8jUGpR6SOXjqkz5w4m/Aflq6J82aVr2AU4P9nlauJugdS+FVopQhlEDradAAAAAElFTkSuQmCC';

{ TSignatureCapture }

procedure TSignatureCapture.ButtonChanged(Sender: TObject);
begin
  if FClearButton.Image.Data <> '' then
    FClearBtn.LoadFromResource('')
  else
    FClearBtn.LoadFromFile(FClearButton.Image.FileName);
    
  Invalidate;
end;

procedure TSignatureCapture.ClearBtnChanged(Sender: TObject);
begin
  Invalidate;
end;

procedure TSignatureCapture.CreateInitialize;
begin
  inherited;
  FMinSignaturePoints := 1;
  AllowTouch := False;
  FClearBtn := TGraphic.Create;
  FClearBtn.LoadFromResource(TSIGNATURECAPTURECLEAR);
  FClearBtn.OnChange := ClearBtnChanged;
  FMouseDown := False;
  FClearButton := TSignatureCaptureClearButton.Create;
  FClearButton.OnChange := ButtonChanged;
  FSignaturePoints := TSignatureCapturePoints.Create;
  FTextPosition := TSignatureCaptureSignTextPosition.Create;
  FTextPosition.OnChange := TextPositionChanged;
  FPen := TPen.Create;
  CustomBorder := True;
  BorderStyle := bsSingle;
  Width := 300;
  Height := 256;
end;

destructor TSignatureCapture.Destroy;
begin
  FClearBtn.Free;
  FClearButton.Free;
  FSignaturePoints.Free;
  FTextPosition.Free;
  FPen.Free;
  inherited;
end;

function TSignatureCapture.GetAsBase64Image: string;
begin
  FExport := True;
  Invalidate;
  Result := Canvas.GetBase64Image;
  FExport := False;
  Invalidate;
end;

function TSignatureCapture.GetEmpty: Boolean;
begin
  Result := FSignaturePoints.Count <= FMinSignaturePoints;
end;

function TSignatureCapture.GetLineY: Integer;
begin
  Result := Round(Canvas.TextHeight(Text)) + 2 * TEXTSPACING;
end;

function TSignatureCapture.InClearRect(AX, AY: Integer): Boolean;
var
  h: Integer;
begin
  Result := False;

  case FClearButton.Position of
    cbpTopLeft:
    begin
      case FTextPosition.Position of
        ltpBottomLeft, ltpBottomCenter, ltpBottomRight, ltpXY:
        begin
          Result := (AX >= TEXTSPACING) and (AX <= TEXTSPACING + FClearBtn.Width) and (AY >= TEXTSPACING) and (AY <= TEXTSPACING + FClearBtn.Height);
        end;
        ltpTopLeft, ltpTopCenter, ltpTopRight:
        begin
          h := GetLineY;
          Result := (AX >= TEXTSPACING) and (AX <= TEXTSPACING + FClearBtn.Width) and (AY >= TEXTSPACING + h) and (AY <= TEXTSPACING + h + FClearBtn.Height);
        end;
      end;
    end;
    cbpTopRight:
    begin
      case FTextPosition.Position of
        ltpBottomLeft, ltpBottomCenter, ltpBottomRight, ltpXY:
        begin
          Result := (AX >= Width - FClearBtn.Width - TEXTSPACING) and (AX <= Width - TEXTSPACING) and (AY >= TEXTSPACING) and (AY <= TEXTSPACING + FClearBtn.Height);
        end;
        ltpTopLeft, ltpTopCenter, ltpTopRight:
        begin
          h := GetLineY;
          Result := (AX >= Width - FClearBtn.Width - TEXTSPACING) and (AX <= Width - TEXTSPACING) and (AY >= TEXTSPACING + h) and (AY <= TEXTSPACING + h + FClearBtn.Height);
        end;
      end;
    end;
    cbpBottomLeft:
    begin
      case FTextPosition.Position of
        ltpBottomLeft, ltpBottomCenter, ltpBottomRight:
        begin
          h := GetLineY;
          Result := (AX >= TEXTSPACING) and (AX <= TEXTSPACING + FClearBtn.Width) and (AY >= Height - FClearBtn.Height - h - TEXTSPACING) and (AY <= Height - h - TEXTSPACING);
        end;
        ltpTopLeft, ltpTopCenter, ltpTopRight, ltpXY:
        begin
          Result := (AX >= TEXTSPACING) and (AX <= TEXTSPACING + FClearBtn.Width) and (AY >= Height - FClearBtn.Height - TEXTSPACING) and (AY <= Height - TEXTSPACING);
        end;
      end;
    end;
    cbpBottomRight:
    begin
      case FTextPosition.Position of
        ltpBottomLeft, ltpBottomCenter, ltpBottomRight:
        begin
          h := GetLineY;
          Result := (AX >= Width - FClearBtn.Width - TEXTSPACING) and (AX <= Width - TEXTSPACING) and (AY >= Height - FClearBtn.Height - h - TEXTSPACING) and (AY <= Height - h - TEXTSPACING);
        end;
        ltpTopLeft, ltpTopCenter, ltpTopRight, ltpXY:
        begin
          Result := (AX >= Width - FClearBtn.Width - TEXTSPACING) and (AX <= Width - TEXTSPACING) and (AY >= Height - FClearBtn.Height - TEXTSPACING) and (AY <= Height - TEXTSPACING);
        end;
      end;
    end;
    cbpXY: Result := (AX >= FClearButton.X) and (AX <= FClearButton.X + FClearBtn.Width) and (AY >= FClearButton.Y) and (AY <= FClearButton.Y + FClearBtn.Height);
  end;
end;

procedure TSignatureCapture.MouseDown(Button: TMouseButton; Shift: TShiftState;
  X, Y: Integer);
var
  p: TSignatureCapturePoint;
begin
  inherited;

  if InClearRect(X, Y) then
  begin
    Empty := True;
    if Assigned(OnClear) then
      OnClear(Self);
  end
  else
  begin
    FMouseDown := True;
    p := TSignatureCapturePoint.Create;
    p.X := X;
    p.Y := Y;
    p.DrawLine := False;
    p.PenColor := FPen.Color;
    p.PenWidth := FPen.Width;
    p.PenStyle := FPen.Style;
    FSignaturePoints.Add(p);
    Capture;
  end;
end;

procedure TSignatureCapture.MouseMove(Shift: TShiftState; X, Y: Integer);
var
  p: TSignatureCapturePoint;
begin
  inherited;
  if FMouseDown then
  begin
    Cursor := crDefault;
    p := TSignatureCapturePoint.Create;
    p.X := X;
    p.Y := Y;
    p.DrawLine := True;
    p.PenColor := FPen.Color;
    p.PenWidth := FPen.Width;
    p.PenStyle := FPen.Style;
    FSignaturePoints.Add(p);
    Invalidate;
  end
  else
  begin
    if InClearRect(X, Y) then
      Cursor := crHandPoint
    else
      Cursor := crDefault;
  end;
end;

procedure TSignatureCapture.MouseUp(Button: TMouseButton; Shift: TShiftState; X,
  Y: Integer);
var
  p: TSignatureCapturePoint;
begin
  inherited;
  if not InClearRect(X, Y) then
  begin
    p := TSignatureCapturePoint.Create;
    p.X := X;
    p.Y := Y;
    p.DrawLine := True;
    p.PenColor := FPen.Color;
    p.PenWidth := FPen.Width;
    p.PenStyle := FPen.Style;
    FSignaturePoints.Add(p);
    FMouseDown := False;
    ReleaseCapture;
  end;
end;

procedure TSignatureCapture.Paint;
begin
  if not FExport then
  begin
    inherited;
    Canvas.Clear;
    PaintLine;
    PaintText;
    PaintClearButton;
  end
  else
  begin
    Canvas.Pen.Style := psClear;
    Canvas.FillRect(Bounds(0, 0, Width + 1, Height + 1));
  end;

  PaintSignature(Canvas);
end;

procedure TSignatureCapture.PaintClearButton;
var
  h: Integer;
begin
  case FClearButton.Position of
    cbpTopLeft:
    begin
      case FTextPosition.Position of
        ltpBottomLeft, ltpBottomCenter, ltpBottomRight, ltpXY:
        begin
          Canvas.Draw(TEXTSPACING, TEXTSPACING, FClearBtn);
        end;
        ltpTopLeft, ltpTopCenter, ltpTopRight:
        begin
          h := GetLineY;
          Canvas.Draw(TEXTSPACING, h + TEXTSPACING, FClearBtn);
        end;
      end;
    end;
    cbpTopRight:
    begin
      case FTextPosition.Position of
        ltpBottomLeft, ltpBottomCenter, ltpBottomRight, ltpXY:
        begin
          Canvas.Draw(Width - FClearBtn.Width - TEXTSPACING, TEXTSPACING, FClearBtn);
        end;
        ltpTopLeft, ltpTopCenter, ltpTopRight:
        begin
          h := GetLineY;
          Canvas.Draw(Width - FClearBtn.Width - TEXTSPACING, h + TEXTSPACING, FClearBtn);
        end;
      end;
    end;
    cbpBottomLeft:
    begin
      case FTextPosition.Position of
        ltpBottomLeft, ltpBottomCenter, ltpBottomRight:
        begin
          h := GetLineY;
          Canvas.Draw(TEXTSPACING, Height - FClearBtn.Height - h - TEXTSPACING, FClearBtn);
        end;
        ltpTopLeft, ltpTopCenter, ltpTopRight, ltpXY:
        begin
          Canvas.Draw(TEXTSPACING, Height - FClearBtn.Height - TEXTSPACING, FClearBtn);
        end;
      end;
    end;
    cbpBottomRight:
    begin
      case FTextPosition.Position of
        ltpBottomLeft, ltpBottomCenter, ltpBottomRight:
        begin
          h := GetLineY;
          Canvas.Draw(Width - FClearBtn.Width - TEXTSPACING, Height - FClearBtn.Height - h - TEXTSPACING, FClearBtn);
        end;
        ltpTopLeft, ltpTopCenter, ltpTopRight, ltpXY:
        begin
          Canvas.Draw(Width - FClearBtn.Width - TEXTSPACING, Height - FClearBtn.Height - TEXTSPACING, FClearBtn);
        end;
      end;
    end;
    cbpXY: Canvas.Draw(FClearButton.X, FClearButton.Y, FClearBtn);
  end;
end;

procedure TSignatureCapture.PaintLine;
var
  h: Integer;
begin
  if FTextPosition.LineVisible then
  begin
    h := GetLineY;
    Canvas.Pen.Assign(FTextPosition.Line);
    case FTextPosition.Position of
      ltpBottomLeft, ltpBottomCenter, ltpBottomRight:
      begin
        Canvas.MoveTo(0, Height - h);
        Canvas.LineTo(Width, Height - h);
      end;
      ltpTopLeft, ltpTopCenter, ltpTopRight:
      begin
        Canvas.MoveTo(0, h);
        Canvas.LineTo(Width, h);
      end;
    end;
  end;
end;

procedure TSignatureCapture.PaintSignature(ACanvas: TCanvas);
var
  I: Integer;
begin
  for I := 0 to FSignaturePoints.Count - 2 do
  begin
    if FSignaturePoints[I + 1].DrawLine then
    begin
      ACanvas.Pen.Width := FSignaturePoints[I].PenWidth;
      ACanvas.Pen.Color := FSignaturePoints[I].PenColor;
      ACanvas.Pen.Style := FSignaturePoints[I].PenStyle;
      ACanvas.MoveTo(FSignaturePoints[I].X, FSignaturePoints[I].Y);
      ACanvas.LineTo(FSignaturePoints[I + 1].X, FSignaturePoints[I + 1].Y);
    end;
  end;
end;

procedure TSignatureCapture.PaintText;
var
  r: TRect;
  tw, th: Single;
begin
  tw := Canvas.TextWidth(Text);
  th := Canvas.TextHeight(Text);
  case FTextPosition.Position of
    ltpBottomLeft: r := Rect(TEXTSPACING, Round(Height - th - TEXTSPACING), Round(TEXTSPACING + tw), Height);
    ltpBottomCenter: r := Rect(Round((Width - tw) / 2), Round(Height - th - TEXTSPACING), Round((Width + tw) / 2), Height);
    ltpBottomRight: r := Rect(Round((Width - tw - TEXTSPACING)), Round(Height - th - TEXTSPACING), Width - TEXTSPACING, Height);
    ltpTopLeft: r := Rect(TEXTSPACING, TEXTSPACING, Round(TEXTSPACING + tw), Round(TEXTSPACING + th));
    ltpTopCenter: r := Rect(Round((Width - tw) / 2), TEXTSPACING, Round((Width + tw) / 2), Round(TEXTSPACING + th));
    ltpTopRight: r := Rect(Round((Width - tw - TEXTSPACING)), TEXTSPACING, Width - TEXTSPACING, Round(TEXTSPACING + th));
    ltpXY: r := Rect(FTextPosition.X, FTextPosition.Y, Round(FTextPosition.X + tw), Round(FTextPosition.Y + th));
  end;
  Canvas.TextRect(r, Text);
end;

procedure TSignatureCapture.SetClearButton(
  const Value: TSignatureCaptureClearButton);
begin
  FClearButton.Assign(Value);
end;

procedure TSignatureCapture.SetEmpty(const Value: Boolean);
begin
  if Value then
  begin
    FSignaturePoints.Clear;
    Invalidate;
  end;
end;

procedure TSignatureCapture.SetPen(
  const Value: TPen);
begin
  FPen.Assign(Value);
end;

procedure TSignatureCapture.SetText(const Value: string);
begin
  if FText <> Value then
  begin
    FText := Value;
    Invalidate;
  end;
end;

procedure TSignatureCapture.SetTextPosition(
  const Value: TSignatureCaptureSignTextPosition);
begin
  FTextPosition.Assign(Value);
end;

procedure TSignatureCapture.TextPositionChanged(Sender: TObject);
begin
  Invalidate;
end;

procedure TSignatureCapture.UpdateElement;
begin
  inherited;
  if Assigned(ElementHandle) then
    ElementHandle.style.setProperty('touch-action','none');
end;

{ TSignatureCapturePoints }

function TSignatureCapturePoints.GetItem(
  Index: Integer): TSignatureCapturePoint;
begin
  Result := TSignatureCapturePoint(inherited Items[Index]);
end;

procedure TSignatureCapturePoints.SetItem(Index: Integer;
  const Value: TSignatureCapturePoint);
begin
  inherited Items[Index] := Value;
end;

{ TSignatureCaptureSignTextPosition }

procedure TSignatureCaptureSignTextPosition.Changed;
begin
  if Assigned(OnChange) then
    OnChange(Self);
end;

constructor TSignatureCaptureSignTextPosition.Create;
begin
  FLineVisible := True;
  FPosition := ltpBottomCenter;
  FX := -1;
  FY := -1;
  FLine := TPen.Create;
  FLine.Color := clGray;
  FLine.Width := 1;
end;

procedure TSignatureCaptureSignTextPosition.SetLine(const Value: TPen);
begin
  FLine.Assign(Value);
  Changed;
end;

procedure TSignatureCaptureSignTextPosition.SetLineVisible(const Value: Boolean);
begin
  if FLineVisible <> Value then
  begin
    FLineVisible := Value;
    Changed;
  end;
end;

procedure TSignatureCaptureSignTextPosition.SetPosition(
  const Value: TSignatureCaptureLineTextPosition);
begin
  if FPosition <> Value then
  begin
    FPosition := Value;
    Changed;
  end;
end;

procedure TSignatureCaptureSignTextPosition.SetX(const Value: Integer);
begin
  if FX <> Value then
  begin
    FX := Value;
    Changed;
  end;
end;

procedure TSignatureCaptureSignTextPosition.SetY(const Value: Integer);
begin
  if FY <> Value then
  begin
    FY := Value;
    Changed;
  end;
end;

{ TSignatureCaptureClearButton }

procedure TSignatureCaptureClearButton.Changed;
begin
  if Assigned(OnChange) then
    OnChange(Self);
end;

constructor TSignatureCaptureClearButton.Create;
begin
  FX := -1;
  FY := -1;
  FPosition := cbpBottomLeft;
  FImage := TURLPicture.Create;
  FImage.OnChange := ImageChange;
  FImage.OnDataChange := ImageChange;
end;

destructor TSignatureCaptureClearButton.Destroy;
begin
  FImage.Free;
  inherited;
end;

procedure TSignatureCaptureClearButton.ImageChange(Sender: TObject);
begin
  Changed;
end;

procedure TSignatureCaptureClearButton.SetImage(const Value: TURLPicture);
begin
  FImage := Value;
end;

procedure TSignatureCaptureClearButton.SetPosition(
  const Value: TSignatureCaptureClearButtonPosition);
begin
  if FPosition <> Value then
  begin
    FPosition := Value;
    Changed;
  end;
end;

procedure TSignatureCaptureClearButton.SetX(const Value: Integer);
begin
  if FX <> Value then
  begin
    FX := Value;
    Changed;
  end;
end;

procedure TSignatureCaptureClearButton.SetY(const Value: Integer);
begin
  if FY <> Value then
  begin
    FY := Value;
    Changed;
  end;
end;

end.
